import { Center } from "@chakra-ui/react";
import BaseImage from "@components/BaseImage";
// import Header from "@components/Header";
import UTMLink from "@components/UTMLink";
import { useGainAppNavItems } from "@components/V2/GainMenu";
import {
  menuNavItems,
  useKelpAppMenuItems,
  useKelpAppNavItems,
  useKelpNavItems,
} from "@components/V2/KelpMenu";
import {
  CONNECT_WALLET_CLICK,
  NETWORK_SWITCHED,
  LP_PRODUCT_CTA_CLICK,
} from "@constants/analytics";
import {
  CONNECT_WALLET_CTA,
  toFixedWithoutRounding,
  walletIconMapping,
} from "@constants/common";
import useDappConfig from "@hooks/useDappConfig";
import useMultiChainBalances from "@hooks/useMultiChainBalances";
import { AppHeader } from "@kelp-dao/kernel-design-system";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { updateSelectedChain } from "@store/slices/globalSlice";
import { usePathname, useRouter } from "next/navigation";
import { useRouter as useNextRouter } from "next/router";
import { ReactNode, useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  chainIconMapping,
  getUrlForLocalImage,
  sentryErrorLog,
  trackEvent,
} from "utils";
import { arbitrum, mainnet, unichain } from "viem/chains";
import {
  useAccount,
  useDisconnect,
  useSwitchChain,
  useWalletClient,
} from "wagmi";

const AppLayout = ({ children }: { children: ReactNode }) => {
  const pathname = usePathname();
  const router = useRouter();
  const nextRouter = useNextRouter();
  const KELP_NAV_ITEMS = useKelpNavItems();
  const KELP_APP_NAV_ITEMS = useKelpAppNavItems();
  const GAIN_APP_NAV_ITEMS = useGainAppNavItems();
  const APP_MENU_ITEMS_KELP = useKelpAppMenuItems("kelp");
  const APP_MENU_ITEMS_GAIN = useKelpAppMenuItems("gain");

  // Track previous pathname to detect actual navigation changes
  const prevPathname = useRef<string | null>(null);

  // Check if we're on the 404 page - either by pathname or by Next.js router
  const is404 =
    pathname.includes("/404") || nextRouter.pathname.includes("/404");
  const isBlocked = pathname === "/blocked/";

  const showLandingLayout =
    pathname === "/" ||
    pathname === "/institutions/" ||
    pathname.includes("/insights/resources/") ||
    pathname === "/terms-of-service/" ||
    pathname === "/privacy-policy/";
  // pathname === "/xtragain/mm/";

  // const isNotV2 = pathname === "/xtragain/mm/";

  useEffect(() => {
    // Only log page events on actual navigation (when pathname changes from previous value)
    // This avoids duplicate events with RudderStack's auto page tracking on initial load
    if (prevPathname.current !== null && prevPathname.current !== pathname) {
      if (typeof window !== "undefined" && window !== null) {
        try {
          (window as any).rudderanalytics?.page(undefined, {
            path: pathname,
            url: window.location.href,
          });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log("Error Logging rudderstack page event ", e);
          sentryErrorLog(e);
        }
      }
    }
    prevPathname.current = pathname;
  }, [pathname]);

  const { isConnected, address, connector, chain } = useAccount();
  const { openConnectModal } = useConnectModal();
  const { switchChain } = useSwitchChain();
  const { data: walletClient } = useWalletClient();
  const { disconnect } = useDisconnect();
  const dispatch = useDispatch();

  // const isV2 =
  //   pathname.includes("v2") ||
  //   pathname.includes("restake") ||
  //   pathname.includes("gain") ||
  //   pathname.includes("xtragain") ||
  //   pathname.includes("bridge-wrap-ageth-v2");

  const isV2Gain =
    pathname.includes("gain") ||
    pathname.includes("xtragain") ||
    pathname.includes("bridge-wrap-ageth");

  const {
    restakeEnabledChains,
    claimEnabledChains,
    vaultEnabledChains,
    vaultSwapEnabledChains,
    swapEnabledChains,
    yieldVaultEnabledChains,
    stableVaultEnabledChains,
    cryptaniumVaultEnabledChains,
    claimBscEnabledChains,
  } = useDappConfig();

  const chainList = useMemo(() => {
    const p = pathname || "";
    const isRestake = p.includes("restake");
    const isAirdropVault = p.includes("airdrop-gain");
    const isYieldVault = p.includes("growth-vault");
    const isStableVault = p.includes("stable-vault");
    const isCryptaniumVault = p.includes("cryptanium-vault");
    const isWrapAgETH = p.includes("bridge-wrap-ageth");
    const isWrap = p.includes("bridge-wrap");
    const isClaimToken = p.includes("claim-token");
    const isWrapOnly = p.includes("wrap") && !isWrapAgETH && !isWrap;

    if (isRestake) return restakeEnabledChains;
    if (isAirdropVault) return vaultEnabledChains;
    if (isYieldVault) return yieldVaultEnabledChains;
    if (isStableVault) return stableVaultEnabledChains;
    if (isCryptaniumVault) return cryptaniumVaultEnabledChains;
    if (isWrapAgETH) return [mainnet, ...vaultSwapEnabledChains];
    if (isWrap) return [mainnet, arbitrum, unichain, ...swapEnabledChains];
    if (isClaimToken) return [...claimEnabledChains, ...claimBscEnabledChains];
    if (isWrapOnly) return [...swapEnabledChains];
    return [mainnet];
  }, [
    pathname,
    restakeEnabledChains,
    claimEnabledChains,
    vaultEnabledChains,
    vaultSwapEnabledChains,
    swapEnabledChains,
    yieldVaultEnabledChains,
    stableVaultEnabledChains,
    cryptaniumVaultEnabledChains,
    claimBscEnabledChains,
  ]);

  // Get balances for all chains in chainList
  const chainIds = (chainList || []).map((c) => c.id);
  const { chainBalances } = useMultiChainBalances(chainIds);

  const connectedWalletInfo = useMemo(() => {
    const chains = (chainList || []).map((c) => {
      const balanceData = chainBalances.find((b) => b.chainId === c.id);
      return {
        label: c.name,
        chainId: String(c.id),
        avatar: chainIconMapping[c.id],
        balance: `${toFixedWithoutRounding(balanceData?.balance || "0", 4)} ${
          c.nativeCurrency.symbol
        }`,
      };
    });

    const walletAvatar =
      (connector && (connector as any).icon) ||
      getUrlForLocalImage(walletIconMapping["injected"]);
    const walletName = connector?.name || "Wallet";

    return {
      address: address || "",
      chains,
      avatar: walletAvatar,
      name: walletName,
      isConnected: Boolean(isConnected && address),
      selectedChainId: chain ? String(chain.id) : undefined,
    };
  }, [address, chain, chainList, connector, isConnected, chainBalances]);

  const handleConnectWallet = () => {
    openConnectModal?.();
    trackEvent(CONNECT_WALLET_CLICK, {
      cta_location: "header",
      cta: CONNECT_WALLET_CTA,
    });
  };

  useEffect(() => {
    dispatch(
      updateSelectedChain({
        name: chain?.name,
        id: chain?.id,
      })
    );
  }, [chain, dispatch]);

  const handleChainSwitch = async (targetChainId: string) => {
    const target = (chainList || []).find(
      (c) => String(c.id) === String(targetChainId)
    );

    if (!target) return;

    const prevChain = chain;
    try {
      await switchChain({ chainId: target.id });
      // Track successful network switch
      trackEvent(NETWORK_SWITCHED, {
        prev_chain_id: prevChain?.id,
        prev_chain: prevChain?.name,
        new_chain_id: target.id,
        new_chain: target.name,
      });
    } catch (error: any) {
      const errorMessage = error?.message || String(error);
      // Try to add chain if it's not configured or if switch fails
      if (
        errorMessage?.includes("Chain not configured") ||
        errorMessage?.includes("chain") ||
        error?.code === 4902
      ) {
        try {
          await walletClient?.addChain({ chain: target as any });

          await switchChain({ chainId: target.id });
          // Track successful network switch after adding chain
          trackEvent(NETWORK_SWITCHED, {
            prev_chain_id: prevChain?.id,
            prev_chain: prevChain?.name,
            new_chain_id: target.id,
            new_chain: target.name,
            chain_added: true,
          });
        } catch (addChainError) {
          console.error("error adding/switching chain", addChainError);
        }
      }
    }
  };

  const selectedExplorerUrl = useMemo(() => {
    const active = chainList.find((c) => c.id === chain?.id) || chainList[0];
    return active?.blockExplorers?.default?.url || "";
  }, [chain?.id, chainList]);

  // const hideWalletButton =
  //   pathname === "/bridge/" ||
  //   pathname === "/bridge-agETH/" ||
  //   pathname === "/avs/";

  if (is404) {
    trackEvent("PAGE_NOT_FOUND", {
      event_type: "page_view",
      event_category: "Page Not Found",
      app: "kelp",
      url: pathname,
    });

    if (typeof window !== "undefined") {
      window.location.replace("https://kerneldao.com/404/");
    }

    return null;
  }

  if (showLandingLayout || isBlocked) {
    return (
      <div className="h-full relative w-full bg-[#F4F4F7]">
        <div className="h-full relative w-full">
          {!isBlocked && (
            <Center
              flexDir="column"
              position="sticky"
              top={0}
              zIndex={20}
              width="100%"
            >
              <AppHeader
                className="w-full"
                appType={"kelp"}
                onPrimaryAction={() => {
                  trackEvent(LP_PRODUCT_CTA_CLICK, {
                    location: "header",
                    button_text: "Restake",
                  });
                  router.push("/restake");
                }}
                onLogoClick={() =>
                  window.open("https://kerneldao.com", "_self")
                }
                navItems={KELP_NAV_ITEMS}
                appMenuItems={APP_MENU_ITEMS_KELP}
                isLandingPage={true}
              />
            </Center>
          )}
          {children}
        </div>
      </div>
    );
  }

  // if (isNotV2) {
  //   return (
  //     <div className="h-full relative w-full">
  //       <Header
  //         secondaryVariant={false}
  //         kelplogo={true}
  //         hideWalletButton={hideWalletButton}
  //       />
  //       {children}
  //     </div>
  //   );
  // }

  // if (isV2) {
  //   const isUnsupportedNetwork =
  //     chain &&
  //     chainList &&
  //     chainList.find((c) => c.id === chain?.id) === undefined;

  //   return (
  //     <div className="min-h-screen relative w-full bg-[#F4F4F7]">
  //       <div className="min-h-screen relative w-full flex flex-col gap-10 md:gap-16">
  //         <AppHeader
  //           className="w-full sticky top-0 z-50"
  //           appType={"kelp"}
  //           onPrimaryAction={() => router.push("/restake")}
  //           onLogoClick={() => router.push("https://kerneldao.com")}
  //           navItems={KELP_APP_NAV_ITEMS}
  //           appMenuItems={APP_MENU_ITEMS}
  //           isLandingPage={false}
  //           connectedWalletInfo={connectedWalletInfo}
  //           onConnectWallet={handleConnectWallet}
  //           onConnectWalletMenu={() => {}}
  //           onChainSwitch={handleChainSwitch}
  //           walletName={connector?.name}
  //           blockExplorerUrl={selectedExplorerUrl}
  //           shortenSpaceID={undefined}
  //           onDisconnect={() => disconnect()}
  //           menuNavItems={menuNavItems}
  //           ImageComponent={BaseImage}
  //           isUnsupportedNetwork={isUnsupportedNetwork}
  //         />
  //         <div className="w-full max-w-[1536px] mx-auto px-4 md:!px-6 lg:!px-8 xl:!px-20 mb-10">
  //           {children}
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  const isUnsupportedNetwork =
    chain &&
    chainList &&
    chainList.find((c) => c.id === chain?.id) === undefined;

  return (
    <>
      <div className="min-h-screen relative w-full bg-[#F4F4F7]">
        <div className="min-h-screen relative w-full flex flex-col gap-10 md:gap-16">
          <AppHeader
            className="w-full sticky top-0 z-50"
            appType={isV2Gain ? "gain" : "kelp"}
            onPrimaryAction={() => {
              trackEvent(LP_PRODUCT_CTA_CLICK, {
                location: "header",
                button_text: "Restake",
              });
              router.push("/restake");
            }}
            onLogoClick={() =>
              router.push("https://kerneldao.com" + (isV2Gain ? "/gain" : ""))
            }
            navItems={isV2Gain ? GAIN_APP_NAV_ITEMS : KELP_APP_NAV_ITEMS}
            appMenuItems={isV2Gain ? APP_MENU_ITEMS_GAIN : APP_MENU_ITEMS_KELP}
            isLandingPage={false}
            connectedWalletInfo={connectedWalletInfo}
            onConnectWallet={handleConnectWallet}
            onConnectWalletMenu={() => {}}
            onChainSwitch={handleChainSwitch}
            walletName={connector?.name}
            blockExplorerUrl={selectedExplorerUrl}
            shortenSpaceID={undefined}
            onDisconnect={() => disconnect()}
            menuNavItems={menuNavItems}
            ImageComponent={BaseImage}
            isUnsupportedNetwork={isUnsupportedNetwork}
            LinkComponent={UTMLink}
          />
          <div
            className={`w-full max-w-[1536px] mx-auto px-4 md:!px-6 lg:!px-8 xl:!px-20 mb-10`}
          >
            {children}
          </div>
        </div>
      </div>
    </>
  );
};

export default AppLayout;
